import React, { useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import UsuariosServiceAPI from '../../services/usuariosServiceAPI';
import { useAuth } from '../../contexts/AuthContext';
import RedefinirSenhaModal from './RedefinirSenhaModal';

interface EsqueciSenhaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EsqueciSenhaModal: React.FC<EsqueciSenhaModalProps> = ({ isOpen, onClose }) => {
  const { login } = useAuth();
  const [emailOrUser, setEmailOrUser] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [senhaTemporaria, setSenhaTemporaria] = useState('');
  const [usuarioEncontrado, setUsuarioEncontrado] = useState('');
  const [mostrarCampoSenha, setMostrarCampoSenha] = useState(false);
  const [senhaInserida, setSenhaInserida] = useState('');
  const [fazendoLogin, setFazendoLogin] = useState(false);
  const [mostrarRedefinicao, setMostrarRedefinicao] = useState(false);
  const [dadosUsuario, setDadosUsuario] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/solicitar-senha-temporaria`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        },
        body: JSON.stringify({
          email: emailOrUser
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao enviar email');
      }

      setUsuarioEncontrado(emailOrUser);
      setMostrarCampoSenha(true);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar email de recuperação');
    } finally {
      setLoading(false);
    }
  };

  const handleValidarSenhaTemporaria = async () => {
    if (!senhaInserida) {
      setError('Digite a senha temporária recebida');
      return;
    }

    setFazendoLogin(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/validar-senha-temporaria`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        },
        body: JSON.stringify({
          email: emailOrUser,
          senhaTemporaria: senhaInserida
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Senha temporária inválida ou expirada');
      }

      // Senha temporária válida - mostrar tela de redefinição
      setDadosUsuario(data.usuario);
      setMostrarRedefinicao(true);
      setMostrarCampoSenha(false);
    } catch (err: any) {
      setError(err.message || 'Erro ao validar senha temporária');
    } finally {
      setFazendoLogin(false);
    }
  };

  if (mostrarRedefinicao && dadosUsuario) {
    return (
      <RedefinirSenhaModal
        isOpen={isOpen}
        onClose={onClose}
        usuarioId={dadosUsuario.id}
        usuarioNome={dadosUsuario.nome}
        onSuccess={onClose}
      />
    );
  }

  if (success && mostrarCampoSenha) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Senha Temporária Enviada" data-testid="esqueci-senha-modal">
        <div className="py-6">
          <div className="text-center mb-6">
            <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
              <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Senha Temporária Enviada</h3>
            <p className="text-gray-500">
              Enviamos uma senha temporária para o e-mail <span className="font-semibold text-gray-800">{usuarioEncontrado}</span>.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Digite a senha temporária recebida:
              </label>
              <Input
                type="password"
                value={senhaInserida}
                onChange={setSenhaInserida}
                placeholder="Senha temporária"
                error={error}
                dataTestId="senha-temporaria"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleValidarSenhaTemporaria}
                className="flex-1 bg-blue-600"
                loading={fazendoLogin}
                disabled={fazendoLogin}
                dataTestId="entrar-senha-temporaria"
              >
                {fazendoLogin ? 'Verificando...' : 'Validar Senha'}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={fazendoLogin}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Recuperar Senha" data-testid="esqueci-senha-modal">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-xl bg-red-50 p-4 border border-red-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email ou Usuário
          </label>
          <Input
            type="text"
            value={emailOrUser}
            onChange={setEmailOrUser}
            placeholder="Digite seu email ou usuário"
            error={error && !emailOrUser ? 'Campo obrigatório' : ''}
            required
            dataTestId="email-ou-usuario"
          />
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            className="w-full bg-blue-600"
            loading={loading}
            disabled={loading}
          >
            {loading ? 'Enviando...' : 'Enviar Senha Temporária'}
          </Button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EsqueciSenhaModal;
